import { LocalizationProvider }                  from '@mui/x-date-pickers';
import { AdapterMoment } from '@mui/x-date-pickers/AdapterMoment';
import { ThemeProvider as MuiThemeProvider }        from '@mui/material/styles';
import axios, { AxiosError }                        from 'axios';
import { FC, Suspense, useEffect, useState } from 'react';
import { QueryClient, QueryClientProvider }         from 'react-query';
import { HashRouter }                               from 'react-router-dom';
import { ThemeProvider as  ThemeProviders }                            from 'styled-components';
import config                                       from '../config';
import { BaseStyles, muiTheme, theme }              from '../styles';
import { AppRoutes }                                from './AppRoutes';
import { updateAuthToken }                          from './shared/components/AuthWrapper/queries';
import { Notistack }                                from './shared/components/Notistack/Notistack';
import { NotistackProvider }                        from './shared/components/Notistack/NotistackProvider';
import { Loader }                                   from './shared/enhancers/withLoader';
import { logout, setAuthorizationToken } from './shared/utils/setAuthorizationToken';
import { AppUrlListener }                           from './utils/AppUrlListener';
import { App as CapApp }                            from '@capacitor/app';
import { isNative }                                 from './shared/utils/platform';
import { syncBadgeCount }                           from './shared/utils/badge';

axios.defaults.baseURL = config.apiUrl;

axios.defaults.headers.common = {
  'Cache-Control' : 'no-cache, no-store, must-revalidate',
  'Pragma'        : 'no-cache',
  'Expires'       : '0',
  'Accept'        : 'application/json',
};

let updatingToken = false;
let refreshAttempts = 0;
const MAX_REFRESH_ATTEMPTS = 1;

axios.interceptors.response.use(
  undefined,
  (error: AxiosError): Promise<AxiosError> => {
    console.log('Root error', error);

    // Only attempt token refresh if user is authenticated (has a stored token)
    const hasAuthToken = !!localStorage.authToken;

    if (error.response?.status === 401 && hasAuthToken && !updatingToken && refreshAttempts < MAX_REFRESH_ATTEMPTS) {
      updatingToken = true;
      refreshAttempts++;
      updateAuthToken()
        .then(() => {
          console.log('requesting new toekn');
          updatingToken = false;
          refreshAttempts = 0;
          queryClient.invalidateQueries();
        })
        .catch(() => {
          updatingToken = false;
          refreshAttempts = 0;
          console.log('token refresh failed');
          logout();
        });
    } else if (error.response?.status === 401 && hasAuthToken && refreshAttempts >= MAX_REFRESH_ATTEMPTS) {
      refreshAttempts = 0;
      logout();
    }

    return Promise.reject(error);
  }
);

export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry                : 0,
      staleTime            : 0,
      refetchOnWindowFocus : false,
    },
  },
});


export const App: FC = () => {
  const [ isReady, setIsReady ] = useState(false);
  useEffect(
    () => {
      if (localStorage.authToken) {
        setAuthorizationToken(JSON.parse(localStorage.authToken));
      }

      setTimeout(() => {
        setIsReady(true);
      }, 300);

      if (isNative) {
        if (!localStorage.authToken) {
          syncBadgeCount(0);
        }

        CapApp.addListener('appStateChange', ({ isActive }) => {
          if (isActive) {
            queryClient.invalidateQueries('CHATS');
          }
        });
      }

      return () => {
        if (isNative) CapApp.removeAllListeners();
      };
    }, []
  );

  const routerContent = (
    <>
      <AppUrlListener />

      { isReady && <AppRoutes /> }
    </>
  );

  return (
    <Suspense fallback={<Loader absolute />}>
      <LocalizationProvider dateAdapter={AdapterMoment}>
        <MuiThemeProvider theme={muiTheme}>
          <ThemeProviders theme={{ ...muiTheme, ...theme }}>
            <QueryClientProvider client={queryClient}>
              <BaseStyles />
              <NotistackProvider>
                <HashRouter>{ routerContent }</HashRouter>
                <Notistack />
              </NotistackProvider>
            </QueryClientProvider>
          </ThemeProviders>
        </MuiThemeProvider>
      </LocalizationProvider>
    </Suspense>
  );
};

export default App;
